import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError, api } from "./api";
import type { User } from "./types";

interface SessionValue {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called when any request 401s, so an expired session drops straight to login. */
  clear: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .me()
      .then((result) => active && setUser(result.user))
      .catch(() => active && setUser(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await api.login(username, password);
    setUser(result.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const clear = useCallback(() => setUser(null), []);

  return (
    <SessionContext.Provider value={{ user, loading, signIn, signOut, clear }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}

/**
 * Runs an API call, dropping to the login screen if the session has expired.
 *
 * Ninety-day sessions mean this is rare, but when it happens mid-trip the failure
 * has to be legible rather than a screen of errors.
 */
export function useApiErrorHandler() {
  const { clear } = useSession();
  return useCallback(
    (error: unknown): string => {
      if (error instanceof ApiError) {
        if (error.isAuth) clear();
        return error.message;
      }
      return error instanceof Error ? error.message : "Da ist etwas schiefgelaufen.";
    },
    [clear],
  );
}
